/**
 * ICU/cEEG monitoring workbench helpers — compose cadence SLAs, escalation,
 * multi-panel trend→raw layout, and patient-specific seizure template screening
 * into a single hospital-OS surface model (Epic B1).
 */

import {
  computeCeegCadenceStatus,
  type CeegCadenceStatus,
} from "@features/Report/utils/ceegCadence";
import {
  canTakeReportImpactingAction,
  confirmIcuEscalation,
  createIcuEscalationEvent,
  type IcuEscalationEvent,
  type IcuEscalationKind,
} from "./consensusAndIcuEscalation";
import {
  computeCeegSlaTimers,
  type CeegSlaTimers,
} from "./clinicianOpsAndEvidence";
import {
  createDefaultTrendPanelConfig,
  type QeegTrendPanelConfig,
} from "./qeegTrendPanelConfig";
import { createDefaultQeegRawLayout } from "./qeegRawLinkedLayout";
import type { QeegRawLinkedLayout } from "./qeegRawLinkedLayout";
import {
  matchCandidateToSeizureTemplates,
  tuneScreeningThresholdWithTemplates,
  type PatientSeizureTemplate,
  type TemplateMatchScore,
} from "./detectorPerfAndSeizureTemplates";

export const ICU_CEEG_WORKBENCH_SCHEMA_VERSION = "icu-ceeg-workbench-v1.0.0";

export type IcuWorkbenchStudyRow = {
  studyId: string;
  patientLabel: string;
  recordingId?: string | null;
  monitoringStartedAtIso: string;
  firstReviewCompletedAtIso?: string | null;
  lastReportAtIso?: string | null;
  lastReviewedAtIso?: string | null;
  oldestPendingCandidateIso?: string | null;
  significantChange?: boolean;
  pendingEscalations?: IcuEscalationEvent[];
};

export type IcuWorkbenchStudyView = {
  studyId: string;
  patientLabel: string;
  recordingId: string | null;
  cadence: CeegCadenceStatus;
  sla: CeegSlaTimers;
  reportActionsAllowed: boolean;
  first60Overdue: boolean;
  report24hOverdue: boolean;
  escalationOverdue: boolean;
  openEscalationCount: number;
};

export type IcuComplianceDashboard = {
  schemaVersion: string;
  generatedAtIso: string;
  totalStudies: number;
  first60OverdueCount: number;
  report24hOverdueCount: number;
  escalationOverdueCount: number;
  reportActionsBlockedCount: number;
  studies: IcuWorkbenchStudyView[];
};

export function buildIcuWorkbenchStudyView(
  row: IcuWorkbenchStudyRow,
  now: Date = new Date(),
  cadenceConfig?: Partial<{
    firstReviewMinutesMax: number;
    reportCadenceHours: number;
  }>,
): IcuWorkbenchStudyView {
  const cadence = computeCeegCadenceStatus({
    monitoringStartedAt: row.monitoringStartedAtIso,
    firstReviewCompletedAt: row.firstReviewCompletedAtIso,
    lastReportAt: row.lastReportAtIso,
    significantChange: row.significantChange,
    now,
    config: cadenceConfig
      ? {
          firstReviewMinutesMax: cadenceConfig.firstReviewMinutesMax,
          reportCadenceHours: cadenceConfig.reportCadenceHours,
        }
      : undefined,
  });
  const sla = computeCeegSlaTimers({
    studyStartIso: row.monitoringStartedAtIso,
    lastReviewedAtIso: row.lastReviewedAtIso,
    oldestPendingCandidateIso: row.oldestPendingCandidateIso,
    escalationSlaMinutes: cadenceConfig?.firstReviewMinutesMax,
    now,
  });
  const pending = row.pendingEscalations || [];
  return {
    studyId: row.studyId,
    patientLabel: row.patientLabel,
    recordingId: row.recordingId ?? null,
    cadence,
    sla,
    reportActionsAllowed: canTakeReportImpactingAction(pending),
    first60Overdue: cadence.firstReviewOverdue,
    report24hOverdue: cadence.next24hReportOverdue,
    escalationOverdue: sla.escalationOverdue,
    openEscalationCount: pending.filter(
      (event) => event.requiresConfirmation && !event.confirmed,
    ).length,
  };
}

export function buildIcuComplianceDashboard(
  rows: IcuWorkbenchStudyRow[],
  now: Date = new Date(),
  cadenceConfig?: Partial<{
    firstReviewMinutesMax: number;
    reportCadenceHours: number;
  }>,
): IcuComplianceDashboard {
  const studies = rows.map((row) =>
    buildIcuWorkbenchStudyView(row, now, cadenceConfig),
  );
  return {
    schemaVersion: ICU_CEEG_WORKBENCH_SCHEMA_VERSION,
    generatedAtIso: now.toISOString(),
    totalStudies: studies.length,
    first60OverdueCount: studies.filter((s) => s.first60Overdue).length,
    report24hOverdueCount: studies.filter((s) => s.report24hOverdue).length,
    escalationOverdueCount: studies.filter((s) => s.escalationOverdue).length,
    reportActionsBlockedCount: studies.filter((s) => !s.reportActionsAllowed)
      .length,
    studies,
  };
}

export type IcuWorkbenchSurfaceModel = {
  schemaVersion: string;
  dashboard: IcuComplianceDashboard;
  trendPanelConfig: QeegTrendPanelConfig;
  rawLinkedLayout: QeegRawLinkedLayout;
};

export function createIcuWorkbenchSurface(
  rows: IcuWorkbenchStudyRow[],
  now: Date = new Date(),
  cadenceConfig?: Partial<{
    firstReviewMinutesMax: number;
    reportCadenceHours: number;
  }>,
): IcuWorkbenchSurfaceModel {
  return {
    schemaVersion: ICU_CEEG_WORKBENCH_SCHEMA_VERSION,
    dashboard: buildIcuComplianceDashboard(rows, now, cadenceConfig),
    trendPanelConfig: createDefaultTrendPanelConfig("icu_workbench"),
    rawLinkedLayout: createDefaultQeegRawLayout(),
  };
}

export function raiseIcuWorkbenchEscalation(input: {
  eventId: string;
  studyId: string;
  kind: IcuEscalationKind;
  message: string;
  requiresConfirmation?: boolean;
  at?: Date;
}): IcuEscalationEvent {
  return createIcuEscalationEvent(input);
}

export function acknowledgeIcuWorkbenchEscalation(
  event: IcuEscalationEvent,
  confirmedBy: string,
): IcuEscalationEvent {
  return confirmIcuEscalation(event, confirmedBy);
}

export type IcuTemplateScreeningResult = {
  matches: TemplateMatchScore[];
  tunedThreshold: number;
  bestTemplateId: string | null;
  screeningAssistActive: boolean;
};

export function screenIcuCandidateWithSeizureTemplates(input: {
  channelSet: string[];
  durationSeconds: number;
  templates: PatientSeizureTemplate[];
  baseThreshold: number;
  admissionId?: string | null;
}): IcuTemplateScreeningResult {
  const scoped = input.admissionId
    ? input.templates.filter((t) => t.admissionId === input.admissionId)
    : input.templates;
  const matches = matchCandidateToSeizureTemplates({
    channelSet: input.channelSet,
    durationSeconds: input.durationSeconds,
    templates: scoped,
  });
  const tunedThreshold = tuneScreeningThresholdWithTemplates({
    baseThreshold: input.baseThreshold,
    matches,
  });
  const best = matches
    .filter((m) => m.matched)
    .sort((a, b) => b.score - a.score)[0];
  return {
    matches,
    tunedThreshold,
    bestTemplateId: best?.templateId ?? null,
    screeningAssistActive: Boolean(best),
  };
}
